'use strict';

import { isRTL } from '@borngroup/born-utilities';

import { selectFeaturedCarouselItem } from '../../../converse_core/cartridge/js/born/modules/carousel-settings/featured-carousel';

/** @type {string} */
const FS_PROMO_BOUND_ATTR = 'data-fs-promo-bound';

/**
 * @typedef {Object} FirstSpiritCarouselTypeConfig
 * @property {Object} options - Slick options for the carousel type.
 * @property {Object<string, Function>} [methods] - Slick event handlers.
 */

/**
 * Returns the mobile breakpoint used by Born responsive slick settings.
 *
 * @returns {number}
 * @private
 */
function _getMobileBreakpoint() {
    if (
        window.Constants &&
        window.Constants.deviceBreakpoints &&
        window.Constants.deviceBreakpoints.BREAKPOINT_PX_SM
    ) {
        return window.Constants.deviceBreakpoints.BREAKPOINT_PX_SM;
    }

    return 767;
}

/**
 * Returns the desktop breakpoint used by Born responsive slick settings.
 *
 * @returns {number}
 * @private
 */
function _getTabletBreakpoint() {
    if (
        window.Constants &&
        window.Constants.deviceBreakpoints &&
        window.Constants.deviceBreakpoints.BREAKPOINT_PX_MD
    ) {
        return window.Constants.deviceBreakpoints.BREAKPOINT_PX_MD;
    }

    return 1024;
}

/**
 * Returns the desktop breakpoint used by Born responsive slick settings.
 *
 * @returns {number}
 * @private
 */
function _getDesktopBreakpoint() {
    if (
        window.Constants &&
        window.Constants.deviceBreakpoints &&
        window.Constants.deviceBreakpoints.BREAKPOINT_PX_LG
    ) {
        return window.Constants.deviceBreakpoints.BREAKPOINT_PX_LG;
    }

    return 1440;
}

/**
 * Builds slick arrow markup for global promo banner carousels.
 *
 * @param {string} name - Icon name.
 * @param {string} direction - Arrow direction (`prev` or `next`).
 * @param {string} arrowClass - CSS classes for the button.
 * @returns {string}
 * @private
 */
function _generatePromoArrow(name, direction, arrowClass) {
    let resources = window.Resources || {};
    let directionKey = direction.toUpperCase();
    let label = resources[`GO_${directionKey}`] || direction;

    return (
        `<button type="button" class="${arrowClass} slick-${
            direction
        }" title="${label}" aria-label="${
            label
        }" data-qa="global-promo-banner__controls--${direction}">` +
        '<svg class="icon icon--medium" role="none" focusable="false" aria-hidden="true">' +
        `<use xlink:href="#icon--${name}"/>` +
        '</svg>' +
        '<svg class="adobe-target__chevron hidden" role="none" focusable="false" aria-hidden="true">' +
        '<use xlink:href="#icon--chevron"/>' +
        '</svg>' +
        '</button>'
    );
}

/**
 * Born carousel type registry for legacy FirstSpirit markup.
 *
 * Audited FS content library types: `featuredCarousel` (landing pages),
 * `globalPromoBanner` (shared library assets). Other Born types are not ported
 * unless they appear in embedded FS assets.
 *
 * @type {Object<string, FirstSpiritCarouselTypeConfig>}
 */
const CAROUSEL_TYPES = {
    featuredCarousel: {
        options: {
            arrows: false,
            infinite: true,
            mobileFirst: false,
            fade: true,
            verticalSwiping: true,
            autoplay: true,
            speed: 500,
            initialSlide: 0,
            reslickResponsively: true,
            waitForAnimate: false,
            responsive: [
                {
                    breakpoint: _getMobileBreakpoint(),
                    settings: 'unslick',
                },
            ],
        },
        methods: {
            beforeChange: selectFeaturedCarouselItem,
            init: _initFirstSpiritFeaturedCarousel,
        },
    },
    globalPromoBanner: {
        options: {
            slidesToShow: 1,
            arrows: true,
            infinite: true,
            autoplaySpeed: 5000,
            mobileFirst: false,
            autoplay: true,
            responsiveSlideVisibility: true,
            prevArrow: _generatePromoArrow(
                'arrow',
                'prev',
                'global-promo-banner__carousel__arrow--prev global-promo-banner__carousel__arrow icon--rotate-h display--small-up',
            ),
            nextArrow: _generatePromoArrow(
                'arrow',
                'next',
                'global-promo-banner__carousel__arrow--next global-promo-banner__carousel__arrow display--small-up',
            ),
            responsive: [
                {
                    breakpoint: _getMobileBreakpoint(),
                    settings: 'refresh',
                },
                {
                    breakpoint: _getDesktopBreakpoint(),
                    settings: 'refresh',
                },
            ],
        },
        methods: {
            init: _initFirstSpiritGlobalPromoSetup,
        },
    },
};

/**
 * Binds featured-carousel hover controls for one slick instance and replaces
 * any prior handlers when responsive reslick recreates the carousel.
 *
 * @param {JQuery.Event} evt - Slick init event.
 * @param {JQuery} $slick - Slick instance.
 * @returns {void}
 * @private
 */
function _initFirstSpiritFeaturedCarousel(evt, $slick) {
    let $featuredCarousel = $slick.$slider.closest('.featured-carousel');

    if (!$featuredCarousel.length) {
        return;
    }

    let carouselControls = $featuredCarousel[0].querySelectorAll(
        '[data-single-slide]',
    );

    [].forEach.call(carouselControls, (singleControl, index) => {
        if (singleControl._fsFeaturedHandlers) {
            singleControl.removeEventListener(
                'mouseenter',
                singleControl._fsFeaturedHandlers.gotoIndex,
            );
            singleControl.removeEventListener(
                'mouseenter',
                singleControl._fsFeaturedHandlers.pause,
            );
            singleControl.removeEventListener(
                'mouseleave',
                singleControl._fsFeaturedHandlers.play,
            );
        }

        function gotoIndex() {
            let currentIndex = $slick.slickCurrentSlide();

            if (currentIndex != index - 1) {
                $slick.slickGoTo(index - 1);
            }
        }

        function pause() {
            $slick.slickPause();
        }

        function play() {
            $slick.slickPlay();
        }

        singleControl._fsFeaturedHandlers = {
            gotoIndex: gotoIndex,
            pause: pause,
            play: play,
        };

        singleControl.addEventListener('mouseenter', gotoIndex);
        singleControl.addEventListener('mouseenter', pause);
        singleControl.addEventListener('mouseleave', play);

        index++;
    });
}

/**
 * Binds global-promo play/pause controls scoped to the current carousel.
 *
 * @param {JQuery.Event} evt - Slick init event.
 * @param {JQuery} $slick - Slick instance.
 * @returns {void}
 * @private
 */
function _initFirstSpiritGlobalPromoSetup(evt, $slick) {
    let promoRoot = $slick.$slider[0].closest('.global-promo-banner');

    if (!promoRoot) {
        return;
    }

    let controls = promoRoot.querySelectorAll('.global-promo-banner__controls');

    for (let i = 0; i < controls.length; i++) {
        let control = controls[i];

        if (control.hasAttribute(FS_PROMO_BOUND_ATTR)) {
            continue;
        }

        let slider = control.parentElement.querySelector(
            '.global-promo-banner__carousel',
        );
        let pause = control.querySelector('.pause');
        let play = control.querySelector('.play');

        if (!slider || !pause || !play) {
            continue;
        }

        control.setAttribute(FS_PROMO_BOUND_ATTR, 'true');

        pause.addEventListener('click', () => {
            slider.slick.pause();
            pause.style.display = 'none';
            play.style.display = 'block';
        });

        play.addEventListener('click', () => {
            slider.slick.play();
            slider.slick.slickNext();
            play.style.display = 'none';
            pause.style.display = 'block';
        });
    }
}

/**
 * Applies Born global-promo slide filters for the current viewport width.
 *
 * @param {JQuery} $carousel - jQuery-wrapped slick root element.
 * @returns {void}
 * @private
 */
function _evaluateSlideVisibilityFilters($carousel) {
    if (!$carousel[0].slick) {
        return;
    }

    let mobileBreakpoint = _getMobileBreakpoint();
    let tabletBreakpoint = _getTabletBreakpoint();

    $carousel[0].slick.slickUnfilter();

    if (window.innerWidth >= mobileBreakpoint) {
        if (window.innerWidth <= tabletBreakpoint) {
            $carousel[0].slick.slickFilter(
                '.slide:not(.display--small-only):not(.display--medium-down):not(.display--medium-up)',
            );
        }

        if (window.innerWidth >= tabletBreakpoint) {
            $carousel[0].slick.slickFilter(
                '.slide:not(.display--medium-only):not(.display--small-only):not(.display--medium-down)',
            );
        }
    } else {
        $carousel[0].slick.slickFilter(
            '.slide:not(.display--medium-only):not(.display--medium-up):not(.display--small-up)',
        );
    }
}

/**
 * Re-applies global-promo slide visibility filters on resize.
 *
 * @param {JQuery} $carousel - jQuery-wrapped slick root element.
 * @returns {void}
 * @private
 */
function _bindResponsiveSlideVisibility($carousel) {
    _evaluateSlideVisibilityFilters($carousel);

    window.addEventListener('resize', () => {
        _evaluateSlideVisibilityFilters($carousel);
    });
}

/**
 * Re-initializes a carousel when Born responsive settings require reslick.
 *
 * @param {JQuery} $carousel - jQuery-wrapped slick root element.
 * @param {Object} carouselSettings - Parsed slick settings.
 * @returns {void}
 * @private
 */
function _bindReslickResponsively($carousel, carouselSettings) {
    let mobileFirst = carouselSettings.mobileFirst;
    let responsiveSettings = carouselSettings.responsive;

    if (!responsiveSettings || !responsiveSettings.length) {
        return;
    }

    window.addEventListener('resize', () => {
        for (let i = 0; i < responsiveSettings.length; i++) {
            let child = responsiveSettings[i];
            let shouldReslick =
                (window.innerWidth <= child.breakpoint && mobileFirst) ||
                (window.innerWidth >= child.breakpoint && !mobileFirst);

            if (
                shouldReslick &&
                $carousel[0].slick &&
                $carousel[0].slick.unslicked
            ) {
                _setupCarousel($carousel[0], true);
            }
        }
    });
}

/**
 * Initializes a single legacy FirstSpirit slick carousel element.
 *
 * @param {HTMLElement} carouselEl - Carousel root element.
 * @param {boolean} [reslick=false] - Whether this is a responsive re-init.
 * @returns {boolean} Whether slick initialized successfully.
 * @private
 */
function _setupCarousel(carouselEl, reslick) {
    let $carousel = window.jQuery(carouselEl);
    let slickDataAttr = reslick
        ? carouselEl.getAttribute('data-carousel-config')
        : carouselEl.getAttribute('data-slick') ||
          carouselEl.getAttribute('data-carousel-config');

    if (!slickDataAttr || slickDataAttr === 'null' || slickDataAttr === '') {
        return false;
    }

    let carouselSettings;

    try {
        carouselSettings = JSON.parse(slickDataAttr);
    } catch (e) {
        return false;
    }

    let customConfig =
        carouselSettings && carouselSettings.type
            ? CAROUSEL_TYPES[carouselSettings.type]
            : null;
    let typeSettings =
        carouselSettings &&
        carouselSettings.type &&
        customConfig &&
        customConfig.options
            ? customConfig.options
            : {};
    let defaults = {
        rtl: isRTL() && !carouselSettings.vertical,
        cssEase: carouselSettings.cssEase || 'ease-in-out',
        lazyLoad: 'progressive',
        rows: 0,
    };

    if (!reslick) {
        carouselEl.setAttribute('data-carousel-config', slickDataAttr);
    }

    carouselEl.removeAttribute('data-slick');

    let mergedSettings = window.jQuery.extend(
        {},
        defaults,
        typeSettings,
        carouselSettings,
    );

    if (customConfig && customConfig.methods) {
        for (let key in customConfig.methods) {
            $carousel.off(key).on(key, customConfig.methods[key]);
        }
    }

    try {
        $carousel.slick(mergedSettings);
    } catch (e) {
        return false;
    }

    if (!reslick && mergedSettings.reslickResponsively) {
        _bindReslickResponsively($carousel, mergedSettings);
    }

    if (!reslick && mergedSettings.responsiveSlideVisibility) {
        _bindResponsiveSlideVisibility($carousel);
    }

    return true;
}

/**
 * Copies slick config off `data-slick` and removes the attribute so Born does
 * not claim carousels while FirstSpirit waits for images to load.
 *
 * @param {HTMLElement} root - FirstSpirit content asset container.
 * @returns {void}
 */
export function quarantineFirstSpiritCarousels(root) {
    let carousels = root.querySelectorAll('[data-slick]');

    for (let i = 0; i < carousels.length; i++) {
        let carouselEl = carousels[i];
        let slickDataAttr = carouselEl.getAttribute('data-slick');

        if (
            !slickDataAttr ||
            slickDataAttr === 'null' ||
            slickDataAttr === ''
        ) {
            carouselEl.removeAttribute('data-slick');
            continue;
        }

        if (!carouselEl.getAttribute('data-carousel-config')) {
            carouselEl.setAttribute('data-carousel-config', slickDataAttr);
        }

        carouselEl.removeAttribute('data-slick');
    }
}

/**
 * Initializes all legacy FirstSpirit slick carousels within a content root.
 *
 * @param {HTMLElement} root - FirstSpirit content asset container.
 * @returns {JQuery[]}
 */
export function initFirstSpiritCarousels(root) {
    let initialized = [];

    if (!window.jQuery || !window.jQuery.fn || !window.jQuery.fn.slick) {
        return initialized;
    }

    let carousels = root.querySelectorAll(
        '[data-carousel-config]:not(.slick-initialized)',
    );

    for (let i = 0; i < carousels.length; i++) {
        let carouselEl = carousels[i];

        if (_setupCarousel(carouselEl)) {
            initialized.push(window.jQuery(carouselEl));
        }
    }

    return initialized;
}
